import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional label identifying this boundary for logging (e.g. "Sidebar", "Chat Page") */
  boundaryName?: string;
  /** Optional custom fallback UI to render instead of the default error alert */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorType: 'network' | 'render' | 'unknown';
}

/**
 * Error boundary component that catches render errors in child components.
 * Displays a friendly error message with a retry button.
 * Differentiates between network errors and general render errors.
 *
 * IMPORTANT: This boundary logs error type and component stack only.
 * It does NOT log user data, auth tokens, or request payloads.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorType: 'unknown',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorType = ErrorBoundary.classifyError(error);

    return {
      hasError: true,
      error,
      errorType,
    };
  }

  /**
   * Classifies errors into categories for appropriate user messaging.
   * Network errors get retry-focused messaging; render errors get general messaging.
   */
  private static classifyError(error: Error): 'network' | 'render' | 'unknown' {
    const message = error.message.toLowerCase();
    const name = error.name;

    // Network-related errors
    if (
      (name === 'TypeError' && message.includes('fetch')) ||
      message.includes('network') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('timeout') ||
      message.includes('econnrefused') ||
      message.includes('socket') ||
      message.includes('cors')
    ) {
      return 'network';
    }

    // React render errors (component lifecycle failures)
    if (
      message.includes('cannot read propert') ||
      message.includes('is not a function') ||
      message.includes('is not defined') ||
      message.includes('render') ||
      message.includes('invalid element') ||
      message.includes('minified react error')
    ) {
      return 'render';
    }

    return 'unknown';
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const boundaryLabel = this.props.boundaryName || 'unnamed';

    // Log error classification and component stack for debugging.
    // SAFETY: We intentionally do NOT log error.message in production
    // as it may contain user input or sensitive data from API responses.
    console.error(
      `[ErrorBoundary:${boundaryLabel}] Error type: ${this.state.errorType}`
    );
    console.error(
      `[ErrorBoundary:${boundaryLabel}] Component stack:`,
      errorInfo.componentStack
    );

    // In development, log the full error for easier debugging
    if (import.meta.env.DEV) {
      console.error(`[ErrorBoundary:${boundaryLabel}] Full error:`, error);
    }
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorType: 'unknown' });
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Allow custom fallback UI
    if (this.props.fallback) {
      return this.props.fallback;
    }

    const { errorType, error } = this.state;

    let heading: string;
    let message: string;
    let actionLabel: string;

    switch (errorType) {
      case 'network':
        heading = 'Network connection error';
        message =
          'Unable to connect to the server. Please check your network connection and try again.';
        actionLabel = 'Retry Connection';
        break;
      case 'render':
        heading = 'Display error';
        message =
          'A component failed to render correctly. This is likely a temporary issue. Try again or refresh the page.';
        actionLabel = 'Try Again';
        break;
      default:
        heading = 'Something went wrong';
        message =
          'An unexpected error occurred. Our team has been notified. You can try again or refresh the page.';
        actionLabel = 'Try Again';
    }

    return (
      <div className="px-8 py-12 text-center flex justify-center">
        <div
          role="alert"
          className="border border-wayne-hairline border-l-4 border-l-wayne-red bg-wayne-surface rounded-lg p-5 flex flex-col gap-3 text-left max-w-[640px] w-full"
        >
          <div className="flex items-start gap-2.5">
            <AlertCircle size={18} className="text-wayne-red mt-0.5 shrink-0" />
            <h2 className="text-[16px] font-serif font-medium text-wayne-ink mb-0">
              {heading}
            </h2>
          </div>
          <p className="text-[13px] text-wayne-ink-2">{message}</p>
          {error && import.meta.env.DEV && (
            <code className="text-[12px] font-mono text-wayne-red break-all">
              {error.message}
            </code>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="bg-wayne-surface border border-wayne-hairline hover:bg-wayne-surface-2 text-wayne-ink rounded-full px-5 h-10 text-[13px] font-medium transition-colors"
            >
              {actionLabel}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-wayne-accent hover:text-wayne-ink underline underline-offset-2 text-[13px]"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
